import { makeAutoObservable, makeObservable, observable, runInAction } from "mobx";
import * as frowser from "frowser";
import { AudioContext } from "standardized-audio-context";
import SavedSet from "../../utils/SavedSet";
import SearchState, {
  BASE_URL,
  DISABLE_CACHEING,
  API_BASE_URL,
  CLOUDFRONT_BASE_URL,
} from "../../SearchState";
import { allTags } from "@shared/constants";

/** @typedef {import("../../../sql/schemaDatatypes").MusicSql} MusicSql */
/** @typedef {import("../../../sql/schemaDatatypes").SavedPlaylistSql} SavedPlaylistSql */
/** @typedef {import("@shared/musicEndpointTypes").SiblingsPost} SiblingsPost */
/** @typedef {import("@shared/musicEndpointTypes").SiblingsPostResponse} SiblingsPostResponse */
/** @typedef {import("@shared/typedefs").Tag} Tag */
/** @typedef {import("@shared/typedefs").License} License */
import WaveformDisplayCache from "./WaveformDisplay/WaveformDisplayCache";
import { unmute } from "../../utils/unmute";

export const globalAudioContext = new AudioContext();
unmute(globalAudioContext, true);

export default class MusicTrackState {
  /** @type {MusicTrackState | null} */
  static globallyActiveTrack = null;

  /** @type {Array<MusicTrackState>} */
  static loadedTracks = [];

  /** @type {MusicSql} */
  music;

  /** @type {SearchState} */
  searchState;

  /** @type {MusicTrackState | null} */
  parent;

  isTouch = false;

  loadingAudio = false;

  loadingAudioProgress = 0.0;

  /** @type {AudioBuffer | null} */
  audioBuffer;

  /** @type {Blob | null} */
  audioArrayBuffer;

  /** @type {AudioBufferSourceNode | null} */
  audioSource;

  /** @type {GainNode | null} */
  gainNode;

  playing = false;

  percentage = 0.0;

  /** @type {number} */
  position = 0.0;

  /** @type {number} */
  startTime = 0.0;

  liked = false;

  playlistMenuActive = false;

  trackMenuActive = false;

  hasPlayed = false;

  /** @type {MusicTrackState[]} */
  children = [];

  /** @type {boolean} */
  loadedChildren = false;

  showingAlternativeVersions = false;

  showingAddToPlaylistMenu = false;

  /** @type {HTMLDivElement | null} */
  containerDivRef = null;

  /** @type {HTMLDivElement | null} */
  outerDivRef = null;

  /** @type {number | null} */
  measuredHeight = null;

  /** @type {number | null} */
  measuringHeightAnimationFinished = -1;

  /** @type {boolean} */
  measuringHeightAnimation = false;

  /** @type {WaveformDisplayCache} */
  waveformDisplayCache = new WaveformDisplayCache();

  /** @type {License[]} */
  licenses = [];

  /** @type {boolean} */
  isDragging = false;

  /** @type {number} */
  dragFixedTop = 0;

  /** @type {number} */
  dragFixedLeft = 0;

  /** @type {boolean} */
  setPositionForVideoSync = false;

  /** @type {string} */
  cacheBuster = DISABLE_CACHEING ? `${new Date().getTime()}` : "";

  /**
   * @param {MusicSql} music - The music data object
   * @param {SearchState} searchState - The search state instance
   * @param {MusicTrackState | null} parent - Parent track state if nested
   */
  constructor(music, searchState, parent) {
    makeAutoObservable(this, {}, { enforceActions: "observed" });
    this.isTouch = matchMedia("(hover: none)").matches;
    this.music = music;
    if (typeof this.music.all_tags === "string") {
      this.music.all_tags = /** @type {string} */ (this.music.all_tags).split(
        ",",
      );
    }
    this.searchState = searchState;
    this.liked = SavedSet.isMember("tracks", this.music.id.toString());
    this.hasPlayed = SavedSet.isMember("played", this.music.id.toString());
    this.parent = parent;

    this.children = [];
    this.showingAlternativeVersions = false;

    if (this.music.license_details_json != null) {
      try {
        this.licenses = JSON.parse(this.music.license_details_json);
      } catch (e) {
        // Invalid license JSON, leave blank
      }
    }

    // Have to be backward-compatible

    if (this.licenses.length === 0 && this.music.licensed) {
      /** @type {License} */
      const currentLicense = {
        exclusive: this.music.exclusive,
        expiration: new Date(this.music.license_expiration),
        notes: this.music.notes,
      };
      this.licenses.push(currentLicense);
    }

    // Note: this will make searches slower on admin login and crappy wifi
    if (this.searchState.isAdmin) {
      this.ensureChildrenLoaded();
    }
    if (this.searchState.showingVideoSync) {
      this.setPositionForVideoSync = true;
    }
  }

  showIsExclusive = () => {
    if (!this.searchState.isAdmin) return false;
    if (this.isLicensed()) return true;
    if (this.children != null) {
      for (let i = 0; i < this.children.length; i += 1) {
        if (this.children[i].isLicensed()) return true;
      }
    }
    return false;
  };

  hasChildren = () => {
    if (!this.parent) return true;
    return false;
  };

  hasAnyTags = () => {
    for (let i = 0; i < allTags.length; i += 1) {
      const tag = allTags[i];
      if (
        this.music != null &&
        this.music[tag.urlsafe_name] != null &&
        this.music[tag.urlsafe_name] > 0
      ) {
        return true;
      }
    }
    return false;
  };

  startDragging = () => {
    this.isDragging = true;
  };

  stopDragging = () => {
    this.isDragging = false;
  };

  /**
   * @action
   * @param {number} left
   * @param {number} top
   */
  setDragFixed = (left, top) => {
    this.dragFixedLeft = left;
    this.dragFixedTop = top;
  };

  isLicensed = () => {
    const time = new Date().getTime();
    for (let i = 0; i < this.licenses.length; i += 1) {
      const l = this.licenses[i];
      if (new Date(l.expiration).getTime() > time) return true;
    }
    return false;
  };

  ensureChildrenLoaded = () => {
    if (this.loadedChildren) return;
    if (this.parent != null) return;
    /** @type {SiblingsPost} */ const siblings = {
      versionSearchTerm: this.music.version_search_term,
    };
    fetch(`${API_BASE_URL}/api/v1/siblings`, {
      method: "POST",
      cache: DISABLE_CACHEING ? "reload" : "default",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify(siblings),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((children) => {
        runInAction(() => {
          this.loadedChildren = true;
          this.children = children
            .filter((c) => c.id !== this.music.id)
            .map((child) => new MusicTrackState(child, this.searchState, this));
          // Sort to alphabetical order for children
          this.children.replace(
            this.children
              .slice()
              .sort((a, b) => a.music.name.localeCompare(b.music.name)),
          );
          const anyChildrenLiked =
            this.children.filter((c) => c.liked).length > 0;
          if (anyChildrenLiked) {
            this.searchState.refreshLikes();
          }
        });
      });
  };

  /**
   * @returns {string | null}
   */
  static getActiveAlbumArt() {
    if (MusicTrackState.globallyActiveTrack != null) {
      // console.log(`${MusicTrackState.globallyActiveTrack.music.folder}`);
      // return "https://search.ascheandspencer.com/assets/covers-2024/"+MusicTrackState.globallyActiveTrack.music.folder;
      return `${BASE_URL}/assets/covers-2024/${MusicTrackState.globallyActiveTrack.music.folder}`;
    }
    return null;
  }

  /**
   * @param {MusicSql} music
   * @param {SearchState} searchState
   */
  static getMusicTrackState(music, searchState) {
    return new MusicTrackState(music, searchState);
  }

  getHeight = () => {
    if (this.measuredHeight != null) {
      return this.measuredHeight;
    }

    const visibleChildren = this.getVisibleChildren();
    // const myHeight = (this.showingAlternateVersionsToggle() ? 54 : 35);
    return 35 + visibleChildren.length * 35;
  };

  /**
   * @param {HTMLDivElement | null} ref
   */
  setOuterDivRef = (ref) => {
    this.outerDivRef = ref;
    // if (this.outerDivRef != null) {
    //   this.measuredHeight = this.outerDivRef.getBoundingClientRect().height;
    // }
  };

  /**
   * @param {number} durationMs
   */
  measureResizeAnimation = (durationMs) => {
    this.measuringHeightAnimationFinished = Math.max(
      this.measuringHeightAnimationFinished,
      new Date().getTime() + durationMs,
    );
    if (this.measuringHeightAnimation) return;
    this.measuringHeightAnimation = true;
    const measureStep = () => {
      if (this.outerDivRef != null) {
        runInAction(() => {
          this.measuredHeight = this.outerDivRef.getBoundingClientRect().height;
        });
      }
      if (new Date().getTime() < this.measuringHeightAnimationFinished) {
        requestAnimationFrame(measureStep);
      } else {
        runInAction(() => {
          this.measuringHeightAnimation = false;
        });
      }
    };
    measureStep();
  };

  isParentActive = () => {
    if (
      MusicTrackState.globallyActiveTrack != null &&
      MusicTrackState.globallyActiveTrack !== this
    ) {
      const active = MusicTrackState.globallyActiveTrack;
      if (active.parent != null) {
        const parent = active.parent;
        if (
          (parent === this.parent || this === parent) &&
          parent.showingAlternativeVersions
        ) {
          return true;
        }
      }
    }
    return false;
  };

  ensureGloballyActive = () => {
    if (
      MusicTrackState.globallyActiveTrack != null &&
      MusicTrackState.globallyActiveTrack !== this
    ) {
      const active = MusicTrackState.globallyActiveTrack;
      active.deactivate();
      active.cancelAnyInFlightLoad();
      if (active.showingAlternativeVersions && this.parent !== active) {
        active.toggleShowingAlternativeVersions();
      }
      if (active.parent != null) {
        const parent = active.parent;
        if (
          parent !== this &&
          parent !== this.parent &&
          parent.showingAlternativeVersions
        ) {
          parent.toggleShowingAlternativeVersions();
        }
      }

      // Restart if we're switching from another track
      this.searchState.timeline.reset();
    }
    MusicTrackState.globallyActiveTrack = this;
  };

  showAddToPlaylistMenu = () => {
    this.showingAddToPlaylistMenu = true;
  };

  hideAddToPlaylistMenu = () => {
    this.showingAddToPlaylistMenu = false;
  };

  toggleShowingAlternativeVersions = () => {
    this.showingAlternativeVersions = !this.showingAlternativeVersions;
    // If we're hiding alternates and we're the parent of a selected alternate, be careful
    if (
      !this.showingAlternativeVersions &&
      this.searchState.selectedTrack != null &&
      this.searchState.selectedTrack.parent === this
    ) {
      // If we were selected a child that's now gone, move selection to the parent
      const newVisibleChildren = this.getVisibleChildren();
      if (newVisibleChildren.indexOf(this.searchState.selectedTrack) === -1) {
        this.searchState.selectTrack(this);
      } else {
        // logic revision on desktop - stop playing child if you tuck up the alts
        this.searchState.selectTrack(this.searchState.selectedTrack.parent);
        this.ensureGloballyActive(this.searchState.selectedTrack.parent);
      }
    }
    // if it's a mobile window, make sure the parent is selected, then tuck them all up
    if (
      !this.showingAlternativeVersions &&
      this.searchState.selectedTrack != null &&
      this.searchState.isMobileWindow
    ) {
      this.searchState.selectTrack(this.searchState.selectedTrack);
      this.ensureGloballyActive(this.searchState.selectedTrack);
    }
  };

  getVisibleChildren = () =>
    this.children.filter(
      (child) =>
        this.showingAlternativeVersions ||
        (child.liked && this.searchState.searchType !== "favorites") ||
        child.isGloballyActive(),
    );

  isLoaded = () => this.audioBuffer != null;

  /** @type {Array<() => void>} */
  queuedOnReadyCallbacks = [];

  /** @type {XMLHttpRequest | null} */
  inFlightRequest = null;

  /**
   * @param {MusicSql} music
   */
  copyTagsFrom = (music) => {
    for (let i = 0; i < allTags.length; i += 1) {
      /** @type {Tag} */
      const tag = allTags[i];
      if (music[tag.urlsafe_name] != null) {
        this.music[tag.urlsafe_name] = music[tag.urlsafe_name];
      } else {
        this.music[tag.urlsafe_name] = 0;
      }
    }
  };

  /**
   * @returns {boolean}
   */
  hasTags = () => {
    for (let i = 0; i < allTags.length; i += 1) {
      /** @type {Tag} */
      const tag = allTags[i];
      if (
        this.music[tag.urlsafe_name] != null &&
        this.music[tag.urlsafe_name] > 0
      ) {
        return true;
      }
    }
    return false;
  };

  clearTags = () => {
    for (let i = 0; i < allTags.length; i += 1) {
      /** @type {Tag} */
      const tag = allTags[i];
      this.music[tag.urlsafe_name] = -1;
    }
  };

  /**
   * @param {() => void} [onReady]
   */
  ensureLoaded = (onReady) => {
    this.ensureChildrenLoaded();
    if (this.audioBuffer == null) {
      if (onReady != null) {
        this.queuedOnReadyCallbacks.push(onReady);
      }

      if (this.loadingAudio) {
        return;
      }
      this.loadingAudio = true;
      const request = new XMLHttpRequest();
      this.inFlightRequest = request;
      // http://search.ascheandspencer.com/
      // 'http://localhost/get/music_cors/'+this.music.file_path_high.replace('music/','').replace('.mp3', '')
      // request.open('GET', 'https://search.ascheandspencer.com/get/download/'+this.music.id, true);
      // request.open('GET', BASE_URL+'/'+this.music.file_path_high, true);
      request.open(
        "GET",
        `${CLOUDFRONT_BASE_URL}/${this.music.file_path_high
          .replace(" ", "+")
          .replace("music/", "mp3/")}`,
        true,
      );
      request.responseType = "arraybuffer";

      const progressListener = (evt) => {
        if (evt.lengthComputable) {
          runInAction(() => {
            this.loadingAudioProgress = evt.loaded / evt.total;
          });
        }
      };

      // Adding a progress indicator
      try {
        request.removeEventListener("progress", progressListener);
      } catch (e) {
        console.log(e);
      }
      request.addEventListener("progress", progressListener, false);

      // Decode asynchronously
      request.onload = () => {
        runInAction(() => {
          this.inFlightRequest = null;
        });

        const onError = () => {
          const browser = frowser.getParser(window.navigator.userAgent);
          if (browser.getBrowserName() === "Safari") {
            alert(
              `Safari had an error decoding the "${this.music.name}" audio file. Safari's audio decoder breaks frequently. Please try Chrome or Firefox for a better experience.`,
            );
          } else {
            alert(
              `There was an error decoding the "${this.music.name}" audio file.`,
            );
          }
          runInAction(() => {
            this.loadingAudio = false;
            this.audioBuffer = null;
            this.clearAudioArrayBuffer();
          });
        };

        const decode = () => {
          const response = request.response;
          this.setAudioArrayBuffer(new Blob([response]));

          globalAudioContext.decodeAudioData(
            response,
            (buffer) => {
              runInAction(() => {
                this.audioBuffer = buffer;
                this.loadingAudio = false;
                this.inFlightRequest = null;
                for (let i = 0; i < this.queuedOnReadyCallbacks.length; i += 1) {
                  this.queuedOnReadyCallbacks[i]();
                }
                request.removeEventListener("progress", progressListener, false);
                this.onLoaded();
              });
            },
            onError,
          );
        };
        decode();
      };
      request.send();
    } else if (onReady != null) {
      onReady();
    }
  };

  setAudioArrayBuffer = (data) => {
    this.audioArrayBuffer = data;
  };

  clearAudioArrayBuffer = () => {
    this.audioArrayBuffer = null;
  };

  onLoaded = () => {
    if (MusicTrackState.loadedTracks.indexOf(this) === -1) {
      MusicTrackState.loadedTracks.push(this);
      if (MusicTrackState.loadedTracks.length > 10) {
        // console.log(`Unloading ${MusicTrackState.loadedTracks[0].music.name}`);
        MusicTrackState.loadedTracks[0].unload();
        MusicTrackState.loadedTracks.splice(0, 1);
      }
    }
    if (this.searchState.showingVideoSync) {
      this.setPositionForVideoSync = true;
    }
  };

  resetPositionForVideoSync = () => {
    this.setPositionForVideoSync = false;
  };

  /**
   * This releases the memory we don't need anymore for this track.
   */
  unload = () => {
    if (this.audioSource != null) {
      try {
        this.audioSource.stop();
        this.audioSource.disconnect();
      } catch (e) {
        console.error(e);
      }
    }
    if (this.gainNode != null) {
      try {
        this.gainNode.disconnect();
      } catch (e) {
        console.error(e);
      }
    }
    this.audioSource = null;
    this.gainNode = null;
    this.audioBuffer = null;
    this.clearAudioArrayBuffer();
    this.children = [];
    this.loadedChildren = false;
    this.waveformDisplayCache.unload();
  };

  cancelAnyInFlightLoad = () => {
    if (this.inFlightRequest != null && this.loadingAudio) {
      this.inFlightRequest.abort();
      this.inFlightRequest = null;
      this.loadingAudio = false;
      this.loadingAudioProgress = 0.0;
      // Clear any previous handlers waiting on a load, cause they're likely to want to play
      // the track, which we now don't want to have happen.
      this.queuedOnReadyCallbacks = [];
    }
  };

  showLoadingBar = () =>
    this.loadingAudio && this.queuedOnReadyCallbacks.length > 0;

  /**
   * Plays the music track
   * @action
   */
  play = () => {
    this.searchState.timeline.pause();
    this.ensureGloballyActive();
    this.searchState.selectTrack(this);
    if (!this.searchState.timeline.playing) {
      this.searchState.timeline.play();
    }
    this.searchState.triggerSyncButtonTip();
    this.searchState.hideFavoritesTip();
    this.searchState.logEvent("track-name", "played", `${this.music.name}`);
  };

  connectAudioBuffer = () => {
    if (this.playing) {
      this.stopAudio();
    }
    this.audioSource = globalAudioContext.createBufferSource();
    this.audioSource.buffer = this.audioBuffer;

    this.gainNode = globalAudioContext.createGain();
    this.gainNode.gain.value = this.searchState.musicVolume;
    this.audioSource.connect(this.gainNode);
    this.gainNode.connect(globalAudioContext.destination);
  };

  toggleLiked = () => {
    this.liked = !this.liked;
    SavedSet.setMembership("tracks", this.music.id.toString(), this.liked);
    this.searchState.refreshLikes();
  };

  toggleTrackMenu = () => {
    this.playlistMenuActive = false;
    this.trackMenuActive = !this.trackMenuActive;
  };

  togglePlaylistMenu = () => {
    this.trackMenuActive = false;
    this.playlistMenuActive = !this.playlistMenuActive;
  };

  removeMenus = () => {
    this.playlistMenuActive = false;
    this.trackMenuActive = false;
    console.log(this.playlistMenuActive);
  };

  deactivate = () => {
    // Just as a failsafe, just in case
    this.stopAudio();

    if (MusicTrackState.globallyActiveTrack === this) {
      this.pause();
      MusicTrackState.globallyActiveTrack = null;
    }
    if (this.searchState.showingVideoSync) {
      this.setPositionForVideoSync = true;
    }
  };

  pause = () => {
    this.searchState.timeline.pause();
  };

  /**
   * @param {number} [position]
   */
  startAudio = (position) => {
    this.ensureGloballyActive();
    this.connectAudioBuffer();
    if (position != null) this.position = position;
    this.startTime = globalAudioContext.currentTime - this.position;
    this.audioSource.start(globalAudioContext.currentTime, this.position); // play the source now
    this.hasPlayed = true;
    SavedSet.setMembership("played", this.music.id.toString(), true);
    this.searchState.refreshAnyPlayed();
    this.playing = true;
    this.updatePosition();
  };

  /**
   * Stops the audio playback
   * @action
   */
  stopAudio = () => {
    if (this.audioSource != null) {
      // console.log("attempting to stop audio");
      try {
        this.audioSource.stop(0);
      } catch (e) {
        console.error(e);
      }
      try {
        this.audioSource.disconnect(0);
      } catch (e) {
        console.error(e);
      }
    }
    if (this.gainNode != null) {
      try {
        this.gainNode.disconnect(0);
      } catch (e) {
        console.error(e);
      }
    }

    if (this.audioSource != null) {
      this.audioSource = null;
      this.gainNode = null;
      this.position = globalAudioContext.currentTime - this.startTime;
      this.playing = false;
    }
  };

  /*
isPlaying = () => {
    if (
      this.searchState.showingVideoSync &&
      this.searchState.videoSyncState.videoLoaded
    ) {
      return this.searchState.videoSyncState.playing && this.isGloballyActive();
    }

    return this.playing;
  };
   */

  getPercentage = () => {
    if (!this.isLoaded()) return 0.0;
    if (
      this.searchState.showingVideoSync &&
      this.searchState.videoSyncState.videoLoaded
    ) {
      const videoSyncState = this.searchState.videoSyncState;
      const audioOffset =
        videoSyncState.getAudioOffset(this.music.id) *
        videoSyncState.videoDuration;
      const audioTime = videoSyncState.time - audioOffset;
      let audioPercentage = audioTime / this.audioBuffer.duration;
      audioPercentage = Math.min(Math.max(audioPercentage, 0), 1);
      return audioPercentage;
    }

    return this.percentage;
  };

  updatePosition = () => {
    this.position = this.playing
      ? globalAudioContext.currentTime - this.startTime
      : this.position;

    const duration =
      this.audioBuffer != null
        ? this.audioBuffer.duration
        : this.music.duration;

    if (this.position >= duration) {
      this.position = duration;

      // If we're showing the video sync element, that becomes the single source of truth

      if (
        this.searchState.showingVideoSync &&
        this.searchState.videoSyncState.videoLoaded
      ) {
        // Do nothing
      }

      // Otherwise, we're still the source of truth
      else if (this.playing) {
        this.pause();
        this.searchState.playNextTrack();
      }
    } else if (this.playing) {
      // if we're not at the end yet, keep updating the position every frame while we're
      // still playing the clip
      requestAnimationFrame(this.updatePosition);
    }

    this.percentage = this.position / duration;
  };

  isGloballyActive = () => this === MusicTrackState.globallyActiveTrack;

  showingAlternateVersionsToggle = () =>
    (this.isGloballyActive() || this.getVisibleChildren().length > 0) &&
    this.music.version_search_term != null &&
    this.music.version_search_term !== "";

  isSelected = () => this.searchState.isTrackSelected(this);

  renderPositionText = () => {
    const position = this.getPercentage() * this.audioBuffer.duration;
    const minutes = Math.floor(position / 60);
    const seconds = Math.floor(position - minutes * 60);
    if (seconds < 10) {
      return `${minutes}:0${seconds}`;
    }

    return `${minutes}:${seconds}`;
  };

  download = () => {
    if (!this.searchState.loggedIn) {
      this.searchState.showLoginFlow();
      return;
    }
    this.searchState.acceptTOS(() => {
      this.searchState.showMusicTypesPopup(this.downloadImmediate);
    });
  };

  /**
   * @param {"wav" | "mp3"} musicType
   */
  downloadImmediate = (musicType) => {
    /** @type {HTMLAnchorElement} */
    const linkElement = document.createElement("a");
    if (musicType === "mp3") {
      linkElement.href = `${CLOUDFRONT_BASE_URL}/${this.music.file_path_high
        .replace(new RegExp(" ", "g"), "+")
        .replace("music/", "mp3/")}`;
      linkElement.download = `Asche & Spencer - ${this.music.filename}`;
      this.searchState.logEvent(
        "track-name",
        "downloaded",
        `${this.music.filename}`,
      );
    } else {
      linkElement.href = `${CLOUDFRONT_BASE_URL}/wav/${this.music.filename
        .replace(new RegExp(" ", "g"), "+")
        .replace(".mp3", ".wav")}`;
      linkElement.download = `Asche & Spencer - ${this.music.filename.replace(
        ".mp3",
        ".wav",
      )}`;
      this.searchState.logEvent(
        "track-name-wav",
        "downloaded",
        `${this.music.filename}`,
      );
    }
    linkElement.style = "display: none";
    document.body.appendChild(linkElement);
    linkElement.click();
    linkElement.remove();
    this.searchState.triggerListTip();
  };

  addToSearch = (playlist) => {
    this.searchState.myAccountState.addTrackToPlaylist(playlist, [
      this.music.id.toString(),
    ]);
  };

  createNewSearch = () => {
    this.searchState.saveSingleTrackToNewSearch(this.music.id.toString());
  };
}

makeObservable(MusicTrackState, {
  globallyActiveTrack: observable,
  loadedTracks: observable,
});
